/**
 * CommandPalette — global Cmd+K / Ctrl+K search.
 *
 * Opens on the shortcut from anywhere in the portal, searches across
 * Projects, Contracts, Invoices, Clients, Proposals, Leads, and To-Dos
 * via the /api/global-search endpoint, and navigates to the result on
 * Enter / click.
 *
 * Keyboard:
 *   - Cmd+K / Ctrl+K → toggle open
 *   - Esc           → close
 *   - ↑ / ↓         → move cursor through results
 *   - Enter         → navigate to the highlighted result
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Search, Clock } from 'lucide-react';
import api from '@/lib/api';

const RECENTS_KEY = 'nycd:cmdk:recents';
const RECENTS_MAX = 5;

const readRecents = () => {
  try {
    const raw = localStorage.getItem(RECENTS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const writeRecents = (arr) => {
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(arr.slice(0, RECENTS_MAX)));
  } catch { /* localStorage may be unavailable */ }
};

const TYPE_BADGE = {
  Project: { label: 'Project', cls: 'bg-primary/15 text-primary' },
  Contract: { label: 'Contract', cls: 'bg-violet-500/15 text-violet-400' },
  Invoice: { label: 'Invoice', cls: 'bg-emerald-500/15 text-emerald-400' },
  Client: { label: 'Client', cls: 'bg-cyan-500/15 text-cyan-400' },
  Proposal: { label: 'Proposal', cls: 'bg-amber-500/15 text-amber-400' },
  Lead: { label: 'Lead', cls: 'bg-rose-500/15 text-rose-400' },
  'To-Do': { label: 'To-Do', cls: 'bg-blue-500/15 text-blue-400' },
};

export function CommandPalette({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState(0);
  const [recents, setRecents] = useState([]);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Reset state when the palette opens and refresh recents from localStorage.
  // Use the "adjusting state when a prop changes" pattern to avoid
  // react-hooks/set-state-in-effect on the open transition.
  const [prevOpen, setPrevOpen] = useState(false);
  if (open && !prevOpen) {
    setPrevOpen(true);
    setQuery('');
    setResults([]);
    setCursor(0);
    setRecents(readRecents());
  } else if (!open && prevOpen) {
    setPrevOpen(false);
  }

  useEffect(() => {
    if (open) {
      // Focus input after dialog mounts.
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Debounced search.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = query.trim();
    if (!trimmed) {
      // Schedule the reset asynchronously so it's not a synchronous
      // setState directly inside the effect body (avoids react-hooks/
      // set-state-in-effect; debounced async work is the intended pattern).
      debounceRef.current = setTimeout(() => {
        setResults([]);
        setLoading(false);
      }, 0);
      return () => debounceRef.current && clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get(`/global-search?q=${encodeURIComponent(trimmed)}`);
        setResults(r.data?.results || []);
        setCursor(0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query]);

  const grouped = useMemo(() => {
    const g = {};
    results.forEach((r) => {
      if (!g[r.type]) g[r.type] = [];
      g[r.type].push(r);
    });
    return g;
  }, [results]);

  const navigateToResult = (r) => {
    // Push to recents — dedupe by id, newest first, cap at RECENTS_MAX.
    const next = [r, ...recents.filter((x) => !(x.type === r.type && x.id === r.id))].slice(0, RECENTS_MAX);
    setRecents(next);
    writeRecents(next);
    onOpenChange(false);
    navigate(r.url);
  };

  // The active list is search results when there's a query, recents otherwise.
  const activeList = query.trim() ? results : recents;

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(activeList.length - 1, c + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(0, c - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeList[cursor]) navigateToResult(activeList[cursor]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-xl p-0 overflow-hidden"
        data-testid="command-palette"
        aria-describedby={undefined}
      >
        <DialogTitle className="sr-only">Global search</DialogTitle>
        <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search projects, contracts, invoices, clients, proposals, leads, to-dos…"
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground"
            data-testid="command-palette-input"
          />
          <kbd className="hidden sm:inline-flex items-center text-[10px] font-mono px-1.5 py-0.5 border border-white/15 rounded text-muted-foreground">
            esc
          </kbd>
        </div>

        <div className="max-h-[60vh] overflow-y-auto py-2" data-testid="command-palette-results">
          {!query.trim() && recents.length === 0 && (
            <p className="px-4 py-6 text-center text-xs text-muted-foreground">
              Start typing to search…
            </p>
          )}

          {/* Recent items shown when there is no active query */}
          {!query.trim() && recents.length > 0 && (
            <div className="py-1.5" data-testid="command-palette-recents">
              <p className="px-4 py-1 text-[10px] uppercase tracking-[0.08em] text-muted-foreground/70 flex items-center gap-1.5">
                <Clock className="h-3 w-3" /> Recent
              </p>
              {recents.map((r, idx) => {
                const isActive = idx === cursor;
                return (
                  <button
                    key={`recent-${r.type}-${r.id}`}
                    type="button"
                    onClick={() => navigateToResult(r)}
                    onMouseEnter={() => setCursor(idx)}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-colors ${
                      isActive ? 'bg-white/[0.06]' : 'hover:bg-white/[0.04]'
                    }`}
                    data-testid={`command-palette-recent-${r.type.toLowerCase()}-${r.id}`}
                  >
                    {r.identifier && (
                      <span className="font-mono text-xs text-primary tabular-nums shrink-0 w-24 truncate">
                        {r.identifier}
                      </span>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{r.name || 'Untitled'}</p>
                      {r.subtitle && (
                        <p className="text-xs text-muted-foreground truncate">{r.subtitle}</p>
                      )}
                    </div>
                    <span
                      className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0 ${
                        TYPE_BADGE[r.type]?.cls || ''
                      }`}
                    >
                      {TYPE_BADGE[r.type]?.label || r.type}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {query.trim() && loading && (
            <p className="px-4 py-6 text-center text-xs text-muted-foreground">Searching…</p>
          )}
          {query.trim() && !loading && results.length === 0 && (
            <p className="px-4 py-6 text-center text-xs text-muted-foreground">
              No results for &quot;{query}&quot;
            </p>
          )}
          {query.trim() && Object.entries(grouped).map(([type, items]) => (
            <div key={type} className="py-1.5">
              <p className="px-4 py-1 text-[10px] uppercase tracking-[0.08em] text-muted-foreground/70">
                {TYPE_BADGE[type]?.label || type}
              </p>
              {items.map((r) => {
                const globalIdx = results.indexOf(r);
                const isActive = globalIdx === cursor;
                return (
                  <button
                    key={`${r.type}-${r.id}`}
                    type="button"
                    onClick={() => navigateToResult(r)}
                    onMouseEnter={() => setCursor(globalIdx)}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-left transition-colors ${
                      isActive ? 'bg-white/[0.06]' : 'hover:bg-white/[0.04]'
                    }`}
                    data-testid={`command-palette-result-${r.type.toLowerCase()}-${r.id}`}
                  >
                    {r.identifier && (
                      <span className="font-mono text-xs text-primary tabular-nums shrink-0 w-24 truncate">
                        {r.identifier}
                      </span>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">{r.name || 'Untitled'}</p>
                      {r.subtitle && (
                        <p className="text-xs text-muted-foreground truncate">{r.subtitle}</p>
                      )}
                    </div>
                    <span
                      className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0 ${
                        TYPE_BADGE[r.type]?.cls || ''
                      }`}
                    >
                      {TYPE_BADGE[r.type]?.label || r.type}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between gap-3 px-4 py-2 border-t border-white/10 text-[10px] text-muted-foreground">
          <span className="flex items-center gap-3">
            <span>
              <kbd className="font-mono">↑↓</kbd> navigate
            </span>
            <span>
              <kbd className="font-mono">⏎</kbd> open
            </span>
          </span>
          <span>{activeList.length} {query.trim() ? 'result' : 'recent'}{activeList.length === 1 ? '' : 's'}</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
